import { useEffect, useRef, useState } from "react";
import { Check, X } from "lucide-react";

/**
 * Brief 3.4.10 cutover-finalize Backlog — unified inline confirm pill.
 *
 * Replaces the `window.confirm(...)` pattern across every destructive
 * admin action. Wraps a trigger button: clicking the trigger swaps it
 * for a tiny "Are you sure? · ✓ · ✗" pill that auto-dismisses after
 * `timeoutMs` (default 6s) or on outside click / Escape.
 *
 * Usage:
 *   <InlineConfirm
 *     message="Clear sentinel?"
 *     onConfirm={() => onClear(trigger)}
 *     trigger={({ onClick }) => (
 *       <button onClick={onClick} data-testid="...">
 *         <RotateCcw /> Clear sentinel
 *       </button>
 *     )}
 *   />
 *
 * The trigger render-prop receives `{onClick, armed}` so the caller
 * can hide the trigger or swap copy when armed. Defaults to hiding
 * the trigger and showing only the confirm pill while armed.
 *
 * Accessibility:
 *   • Confirm pill has role="alertdialog" + aria-modal so screen
 *     readers announce it.
 *   • Escape cancels.
 *   • Auto-focus on the confirm button when armed.
 */
export default function InlineConfirm({
  message,
  onConfirm,
  trigger,
  timeoutMs = 6000,
  tone = "default",  // "default" | "danger"
  testid,
  className = "",
}) {
  const [armed, setArmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const containerRef = useRef(null);
  const confirmBtnRef = useRef(null);

  // Auto-cancel + outside click + Escape.
  useEffect(() => {
    if (!armed) return undefined;
    const t = setTimeout(() => setArmed(false), timeoutMs);
    const onDoc = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setArmed(false);
      }
    };
    const onKey = (e) => { if (e.key === "Escape") setArmed(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      clearTimeout(t);
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [armed, timeoutMs]);

  // Focus the confirm button when armed.
  useEffect(() => {
    if (armed && confirmBtnRef.current) {
      confirmBtnRef.current.focus();
    }
  }, [armed]);

  const handleConfirm = async () => {
    setBusy(true);
    try {
      await onConfirm();
    } finally {
      setBusy(false);
      setArmed(false);
    }
  };

  const confirmTone =
    tone === "danger"
      ? "border-rose-400/40 bg-rose-400/[0.10] text-rose-100 hover:bg-rose-400/[0.22]"
      : "border-emerald-400/40 bg-emerald-400/[0.10] text-emerald-100 hover:bg-emerald-400/[0.22]";

  return (
    <span
      ref={containerRef}
      data-testid={testid ? `inline-confirm-${testid}` : "inline-confirm"}
      className={`inline-flex items-center gap-1.5 ${className}`}
    >
      {!armed && trigger({ onClick: () => setArmed(true), armed: false })}
      {armed && (
        <span
          role="alertdialog"
          aria-modal="true"
          aria-label={message}
          className="inline-flex items-center gap-1.5 rounded-md border border-white/[0.10] bg-black/40 px-2 py-1 text-[11px] text-zinc-200"
        >
          <span className="px-1">{message}</span>
          <button
            type="button"
            ref={confirmBtnRef}
            onClick={handleConfirm}
            disabled={busy}
            data-testid={testid ? `inline-confirm-${testid}-yes` : "inline-confirm-yes"}
            className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10.5px] disabled:opacity-40 ${confirmTone}`}
          >
            <Check className="h-2.5 w-2.5" /> Yes
          </button>
          <button
            type="button"
            onClick={() => setArmed(false)}
            disabled={busy}
            data-testid={testid ? `inline-confirm-${testid}-no` : "inline-confirm-no"}
            className="inline-flex items-center gap-1 rounded border border-white/[0.10] px-1.5 py-0.5 text-[10.5px] text-zinc-300 hover:bg-white/[0.04] disabled:opacity-40"
          >
            <X className="h-2.5 w-2.5" /> No
          </button>
        </span>
      )}
    </span>
  );
}
